import {AssetType, OperationModeType, isProducingMode} from '@hconnect/common/types'
import type {Asset, OperationMode} from '@hconnect/common/types'

import type {ScheduleItem} from '../../common/types'
import type {AssetStandardOperationTime} from '../types'

export const groupAndSortAssets = (assets: Asset[]): Record<AssetType, Asset[]> => {
  const assetsByType: Record<AssetType, Asset[]> = {
    [AssetType.CementMill]: [],
    [AssetType.RawMill]: [],
    [AssetType.RotaryKiln]: [],
    [AssetType.CoalMill]: [],
    [AssetType.Crusher]: [],
    [AssetType.Other]: [],
    [AssetType.BaseLoad]: []
  }
  assets.forEach((asset) => {
    assetsByType[asset.type].push(asset)
  })
  Object.entries(assetsByType).forEach(([type, assets]) => {
    assetsByType[type] = assets.sort((a, b) => a.name.localeCompare(b.name))
  })
  return assetsByType
}

export const getOperationModeIdToAssetIdMap = (assets: Asset[]) =>
  assets.reduce<Record<string, number>>((operationModeToAssetsMap, item) => {
    const operationModesToAsset = item.operationModes.reduce<Record<string, number>>(
      (acc, operationMode) => ({...acc, [operationMode.id]: item.id}),
      {}
    )
    return {
      ...operationModeToAssetsMap,
      ...operationModesToAsset
    }
  }, {})

const basicBaseLoadError = 'Please check the base load assets in the Assets configuration.'

/**
 * function to validate if base load asset is added correctly from BE
 */

export const validateBaseLoadInAssets = (assets: Asset[]) => {
  const baseLoadAssets = assets.filter((asset) => asset.type === AssetType.BaseLoad)
  if (baseLoadAssets.length === 0)
    throw new Error(`Plant has no base load specified. ${basicBaseLoadError}`)
  if (baseLoadAssets.length > 1)
    throw new Error(`Plant has more than 1 base load asset specified. ${basicBaseLoadError}`)

  const baseloadOperationModes = baseLoadAssets[0].operationModes.filter(
    (operationMode) => operationMode.type === OperationModeType.Production
  )
  if (baseloadOperationModes.length === 0)
    throw new Error(
      `Base load asset has no valid production operation modes with a base load value. ${basicBaseLoadError}`
    )
  if (baseloadOperationModes.length > 1)
    throw new Error(
      `Base load asset has more than one production operation mode. ${basicBaseLoadError}`
    )
}

/**
 * The function to find standard operation time array with a specific operation mode id
 */
export const getStandardOperationTimes = (
  operationModeId: number,
  standardOperationTimes?: AssetStandardOperationTime[]
) => {
  return (standardOperationTimes ?? []).filter(
    (operationTime) => operationTime.operationModeId === operationModeId
  )
}

/**
 * Function used to select schedule items with production operation mode for silo levels calculations
 * It filters out schedule items with transition times and maintenance modes
 */

export const getScheduleItemsWithProductionModes = (
  items: ScheduleItem[] | undefined,
  operationModesById: Record<string, OperationMode> | undefined
): ScheduleItem[] =>
  items && operationModesById
    ? items
        .filter((item) => item.isTransitionTime === false)
        .filter((item) => {
          const operationMode = operationModesById[item.assetOperationModeId]
          if (!operationMode) {
            throw new Error(
              `BUG: Operation mode with id ${item.assetOperationModeId} doesn't exist in operationModesById dictionary`
            )
          }
          return isProducingMode(operationModesById[item.assetOperationModeId])
        })
    : []
